import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Booking } from '../../types/booking';
import { apiService } from '../../services/api';
import { Toast } from '../../components/common/Toast';
import { ReviewForm } from '../../components/reviews/ReviewForm';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { AdBanner } from '../../components/ads/AdBanner';
import { ADS_ENABLED } from '../../config/ads';

type SortField = 'date' | 'status';
type SortDirection = 'asc' | 'desc';
type StatusFilter = 'all' | 'pending' | 'confirmed' | 'cancelled';

export function BookingHistory() {
  const { t } = useTranslation();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  
  // Filtering and sorting state
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBookingForReview, setSelectedBookingForReview] = useState<number | null>(null);

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.bookings.list();
      setBookings(data);
    } catch (_err) {
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  const handleCancelBooking = async (bookingId: number) => {
    try {
      await apiService.bookings.cancel(bookingId.toString());
      setNotification({
        message: t('bookings.cancelled'),
        type: 'success',
      });
      loadBookings();
    } catch (_err) {
      setNotification({
        message: t('common.error'),
        type: 'error',
      });
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedBookings = useMemo(() => {
    // First filter by status
    let result = bookings;
    
    if (statusFilter !== 'all') {
      result = result.filter(booking => booking.status === statusFilter);
    }
    
    // Then filter by search query (expert email)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(booking => 
        booking.intervieweeEmail?.toLowerCase().includes(query) || false
      );
    }
    
    // Then sort
    return [...result].sort((a, b) => {
      if (sortField === 'date') {
        const dateA = new Date(a.startTime).getTime();
        const dateB = new Date(b.startTime).getTime();
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      } else if (sortField === 'status') {
        const statusOrder = { pending: 0, confirmed: 1, cancelled: 2 };
        const statusA = statusOrder[a.status as keyof typeof statusOrder];
        const statusB = statusOrder[b.status as keyof typeof statusOrder];
        return sortDirection === 'asc' ? statusA - statusB : statusB - statusA;
      }
      return 0;
    });
  }, [bookings, statusFilter, sortField, sortDirection, searchQuery]);

  const handleReviewSubmitted = () => {
    setSelectedBookingForReview(null);
    loadBookings();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-stitch-muted">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center px-4">
        <div className="max-w-md rounded-stitch-lg border border-stitch-danger/40 bg-stitch-danger-soft px-4 py-3 text-center text-stitch-fg-soft">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <h2 className="mb-6 text-xl font-bold text-stitch-fg md:text-2xl">{t('bookings.history')}</h2>

      {ADS_ENABLED && (
        <div className="mb-6">
          <AdBanner slot="booking-history" />
        </div>
      )}

      {/* Filters and Search */}
      <div className="mb-6 rounded-stitch-xl border border-stitch-border bg-stitch-surface p-5 shadow-stitch-card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div>
              <label htmlFor="status-filter" className="block text-sm font-medium text-stitch-fg-soft mb-1">
                {t('bookings.filterByStatus')}
              </label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className="block w-full rounded-stitch-lg border-stitch-border shadow-sm focus:border-stitch-primary focus:ring-stitch-primary"
              >
                <option value="all">{t('bookings.allStatuses')}</option>
                <option value="pending">{t('bookings.status.pending')}</option>
                <option value="confirmed">{t('bookings.status.confirmed')}</option>
                <option value="cancelled">{t('bookings.status.cancelled')}</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="sort-by" className="block text-sm font-medium text-stitch-fg-soft mb-1">
                {t('bookings.sortBy')}
              </label>
              <div className="flex">
                <select
                  id="sort-by"
                  value={sortField}
                  onChange={(e) => handleSort(e.target.value as SortField)}
                  className="block w-full rounded-l-stitch-lg border-stitch-border shadow-sm focus:border-stitch-primary focus:ring-stitch-primary"
                >
                  <option value="date">{t('bookings.date')}</option>
                  <option value="status">{t('bookings.status.label')}</option>
                </select>
                <button
                  onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                  className="flex items-center rounded-r-stitch-lg border border-stitch-border bg-stitch-surface-2 px-2"
                >
                  {sortDirection === 'asc' ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
          
          <div className="w-full md:w-64">
            <label htmlFor="search" className="block text-sm font-medium text-stitch-fg-soft mb-1">
              {t('bookings.searchByInterviewee')}
            </label>
            <input
              type="text"
              id="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('bookings.searchIntervieweePlaceholder')}
              className="block w-full rounded-stitch-lg border-stitch-border shadow-sm focus:border-stitch-primary focus:ring-stitch-primary"
            />
          </div>
        </div>
      </div>

      {filteredAndSortedBookings.length === 0 ? (
        <div className="text-center py-8 text-stitch-muted">
          {statusFilter !== 'all' || searchQuery
            ? t('bookings.noFilteredBookings')
            : t('bookings.noBookings')}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredAndSortedBookings.map((booking) => (
            <div
              key={booking.id}
              className="flex flex-col justify-between rounded-stitch-xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-card md:flex-row md:items-center"
            >
              <div>
                <h3 className="break-all text-lg font-semibold">{booking.intervieweeEmail || 'Unknown'}</h3>
                <div className="text-stitch-muted mt-1">
                  {formatDate(new Date(booking.startTime))} -{' '}
                  {formatTime(new Date(booking.startTime))} -{' '}
                  {formatTime(new Date(booking.endTime))}
                </div>
                <div className="mt-2">
                  <span
                    className={`inline-block px-2 py-1 rounded-full text-sm ${
                      booking.status === 'confirmed'
                        ? 'bg-stitch-success-soft text-stitch-success dark:text-stitch-success'
                        : booking.status === 'pending'
                        ? 'bg-stitch-warning-soft text-stitch-warning dark:text-stitch-warning'
                        : 'bg-stitch-danger-soft text-stitch-danger dark:text-stitch-danger'
                    }`}
                  >
                    {t(`bookings.status.${booking.status}`)}
                  </span>
                </div>
                
                {/* Meeting Link */}
                {booking.status === 'confirmed' && booking.meetingLink && (
                  <div className="mt-2">
                    <a 
                      href={booking.meetingLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-stitch-primary hover:text-stitch-primary-hover"
                    >
                      <span>{t('bookings.joinMeeting')}</span>
                      <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                )}

                {/* Recording Link */}
                {booking.status === 'confirmed' && booking.recordingUrl && (
                  <div className="mt-2">
                    <a 
                      href={booking.recordingUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex max-w-full items-start text-stitch-success hover:opacity-90"
                    >
                      <svg className="mr-1 h-4 w-4 flex-shrink-0 translate-y-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="min-w-0 break-all">{booking.recordingFileName || t('bookings.downloadRecording')}</span>
                    </a>
                  </div>
                )}
              </div>

              <div className="mt-4 md:mt-0 flex space-x-2">
                {booking.status === 'pending' && (
                  <button
                    type="button"
                    onClick={() => handleCancelBooking(booking.id)}
                    className="rounded-stitch-lg border border-stitch-border bg-stitch-surface px-4 py-2 text-sm font-semibold text-stitch-fg transition hover:bg-stitch-surface-2"
                  >
                    {t('bookings.cancel')}
                  </button>
                )}

                {booking.status === 'confirmed' && new Date(booking.endTime) < new Date() && (
                  <button
                    type="button"
                    onClick={() => setSelectedBookingForReview(booking.id)}
                    className="rounded-stitch-lg bg-stitch-primary px-4 py-2 text-sm font-semibold text-white shadow-md shadow-stitch-primary/20 transition hover:bg-stitch-primary-hover"
                  >
                    {t('reviews.leaveReview')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review Form Modal */}
      {selectedBookingForReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stitch-overlay/50 px-4">
          <div className="w-full max-w-2xl rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-modal">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-stitch-fg">{t('reviews.leaveReview')}</h2>
              <button
                type="button"
                onClick={() => setSelectedBookingForReview(null)}
                className="rounded-stitch-lg p-2 text-stitch-muted transition hover:bg-stitch-surface-2 hover:text-stitch-fg"
              >
                <span className="sr-only">{t('common.close')}</span>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            
            {selectedBookingForReview && (
              <ReviewForm
                bookingId={selectedBookingForReview.toString()}
                expertId={filteredAndSortedBookings.find(b => b.id === selectedBookingForReview)?.interviewerId?.toString() || ''}
                expertEmail={filteredAndSortedBookings.find(b => b.id === selectedBookingForReview)?.intervieweeEmail || ''}
                onReviewSubmitted={handleReviewSubmitted}
              />
            )}
          </div>
        </div>
      )}

      {notification && (
        <Toast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
} 